import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslationService } from "@services/translation.service";
import { MultiStateInputCategory } from "@temperature/_enums/reefer/MultiStateInputCategory.enum";
import { ReeferEngineModeCategory } from "@temperature/_enums/reefer/ReeferEngineModeCategory.enum";
import { ReeferEngineSpeedModeCategory } from "@temperature/_enums/reefer/ReeferEngineSpeedModeCategory.enum";
import { ReeferInterfaceStatusCategory } from "@temperature/_enums/reefer/ReeferInterfaceStatusCategory.enum";
import { ReeferOperatingModeCategory } from "@temperature/_enums/reefer/ReeferOperatingModeCategory.enum";
import { ReeferZoneOperatingModeCategory } from "@temperature/_enums/reefer/ReeferZoneOperatingModeCategory.enum";
import { ILatestMultiStateInputRecordQueryOptions } from "@temperature/_models/multiStateInputs/ILatestMultiStateInputRecordQueryOptions";
import { IMultiStateInput, IMultiStateInputWithHistories } from "@temperature/_models/multiStateInputs/IMultiStateInput";
import { IMultiStateInputHistory } from "@temperature/_models/multiStateInputs/IMultiStateInputHistory";
import { IMultiStateInputHistoryQueryOptions } from "@temperature/_models/multiStateInputs/IMultiStateInputHistoryQueryOptions";
import { IMultiStateInputQueryOptions } from "@temperature/_models/multiStateInputs/IMultiStateInputQueryOptions";
import { IMultiStateInputRecord } from "@temperature/_models/multiStateInputs/IMultiStateInputRecord";
import { Observable } from "rxjs";
import { IReeferWithMappings } from "../_models/reefer/IReeferWithMappings";
import { ReeferService } from "./reefer.service";

@Injectable({
    providedIn: 'root'
})
export class MultiStateInputService {
    constructor(
        private readonly http: HttpClient,
        private readonly reeferService: ReeferService,
        private readonly translationService: TranslationService) {
    }

    getMultiStateInputHistories(queryOptions: IMultiStateInputHistoryQueryOptions = {}): Observable<IMultiStateInputHistory[]> {
        return this.http.get<IMultiStateInputHistory[]>(`/api/multistateinputs/history`, { params: queryOptions as HttpParams });
    }

    getMultiStateInputs(queryOptions: IMultiStateInputQueryOptions = {}): Observable<IMultiStateInput[]> {
        return this.http.get<IMultiStateInput[]>(`/api/multistateinputs`, { params: queryOptions as HttpParams });
    }

    getLatestMultiStateInputRecords(queryOptions: ILatestMultiStateInputRecordQueryOptions = {}): Observable<IMultiStateInputRecord[]> {
        return this.http.get<IMultiStateInputRecord[]>(`/api/multistateinputs/records/latest`, { params: queryOptions as HttpParams });
    }

    getVehicleMultiStateInputHistory(vehicleId: number, inputHistory: IMultiStateInputRecord[]): IMultiStateInputRecord[] {
        if (!inputHistory || !inputHistory.length) {
            return;
        }

        const history = inputHistory.filter(item => {
            return item.VehicleId === vehicleId;
        });

        return history;
    }

    getTriggerStateName(triggerState: number, multiStateInputCategory: MultiStateInputCategory): string {
        if (!triggerState || !multiStateInputCategory && multiStateInputCategory != 0) {
            return;
        }

        switch (multiStateInputCategory) {
            case MultiStateInputCategory.ReeferEngineMode:
                return this.translationService.tEnum(ReeferEngineModeCategory, 'ReeferEngineModeCategory', triggerState);

            case MultiStateInputCategory.ReeferEngineSpeedMode:
                return this.translationService.tEnum(ReeferEngineSpeedModeCategory, 'ReeferEngineSpeedModeCategory', triggerState);

            case MultiStateInputCategory.ReeferInterfaceStatus:
                return this.translationService.tEnum(ReeferInterfaceStatusCategory, 'ReeferInterfaceStatusCategory', triggerState);

            case MultiStateInputCategory.ReeferOperatingMode:
                return this.translationService.tEnum(ReeferOperatingModeCategory, 'ReeferOperatingModeCategory', triggerState);

            case MultiStateInputCategory.ReeferZoneOperatingMode:
                return this.translationService.tEnum(ReeferZoneOperatingModeCategory, 'ReeferZoneOperatingModeCategory', triggerState);
        }
    }

    mapHistoriesToInputs(inputs: IMultiStateInputWithHistories[], histories: IMultiStateInputHistory[]): void {
        if (!inputs || !histories) {
            return;
        }
        inputs.forEach(input => {
            input.histories = histories.filter(history => history.MultiStateInputId == input.Id);
        })
    }

    getModes(reeferUnit: IReeferWithMappings, multiStateInputRecords: IMultiStateInputRecord[]) {
        const hardwareSource = this.reeferService.getHardwareSource(reeferUnit.Make);
        reeferUnit.operatingMode = multiStateInputRecords.find(record => record.Category == MultiStateInputCategory.ReeferOperatingMode && record.Source == hardwareSource)?.State;
        reeferUnit.engineMode = multiStateInputRecords.find(record => record.Category == MultiStateInputCategory.ReeferEngineMode && record.Source == hardwareSource)?.State;
    }
}
